<script setup>
import { computed } from 'vue';
import { useApiData } from '../../composables/useApiData';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
  widgetStyle: {
    type: Object,
    default: () => ({}),
  },
});

const { accountId } = useAccount();

const { data, loading, error } = useApiData(
  computed(() => props.widget),
  computed(() => accountId.value)
);

const title = computed(() => props.widget.title);
const deliveryStats = computed(() => {
  // Utiliser les données de l'API si disponibles, sinon les props statiques
  if (data.value?.mapped?.stats) {
    return data.value.mapped.stats;
  }
  if (data.value?.raw?.stats) {
    return data.value.raw.stats;
  }
  return (
    props.widget.properties?.stats || {
      total: 0,
      delivered: 0,
      in_transit: 0,
      pending: 0,
    }
  );
});

const formatNumber = num => new Intl.NumberFormat('fr-FR').format(num || 0);

const statusConfig = [
  {
    status: 'delivered',
    label: 'Livré',
    icon: 'i-lucide-check-circle-2',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50 border-emerald-100',
  },
  {
    status: 'in_transit',
    label: 'En transit',
    icon: 'i-lucide-truck',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 border-blue-100',
  },
  {
    status: 'pending',
    label: 'En attente',
    icon: 'i-lucide-clock',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50 border-amber-100',
  },
];
</script>

<template>
  <div class="h-full flex flex-col group font-inter">
    <div v-if="title" class="mb-6 flex items-center justify-between px-1">
      <h3 class="text-[13px] font-bold text-slate-900 tracking-tight">
        {{ title }}
      </h3>
      <div
        class="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-50 border border-slate-100"
      >
        <span class="i-lucide-truck h-3.5 w-3.5 text-slate-400" />
      </div>
    </div>

    <div v-if="loading" class="flex-1 flex items-center justify-center py-16">
      <div class="flex flex-col items-center gap-4">
        <div
          class="h-10 w-10 border-2 border-slate-100 border-t-slate-950 rounded-full animate-spin"
        />
        <span
          class="text-[11px] font-bold text-slate-400 uppercase tracking-widest"
          >Loading...</span>
      </div>
    </div>

    <div v-else class="flex-1 space-y-2 overflow-auto custom-scrollbar pr-1">
      <div
        v-for="config in statusConfig"
        :key="config.status"
        class="group/item p-4 rounded-xl border border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50/50 transition-all duration-200"
      >
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-4 flex-1 min-w-0">
            <div
              class="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-100 bg-white shadow-sm shrink-0"
            >
              <span class="h-5 w-5" :class="[config.icon, config.color]" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-[13px] font-bold text-slate-900 mb-0.5">
                {{ config.label }}
              </div>
              <div class="text-[11px] font-medium text-slate-400">
                {{ formatNumber(deliveryStats[config.status]) }} orders
              </div>
            </div>
          </div>
          <div class="text-lg font-bold text-slate-900 tracking-tight">
            {{
              Math.round(
                (deliveryStats[config.status] / (deliveryStats.total || 1)) *
                  100
              )
            }}%
          </div>
        </div>
      </div>

      <!-- Total -->
      <div class="pt-4 mt-4 border-t border-slate-100">
        <div
          class="flex items-center justify-between p-4 rounded-xl bg-slate-950 border border-slate-950 shadow-lg shadow-slate-950/10"
        >
          <div class="flex items-center gap-3">
            <span
              class="text-[11px] font-bold text-white uppercase tracking-widest"
              >Total Orders</span>
          </div>
          <div class="text-lg font-bold text-white tracking-tight">
            {{ formatNumber(deliveryStats.total) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
